/**
 * S8's row-menu items (Task 8.10), registered in the shared registry (`toolbar/rowMenuExtensions.ts`, band 800–899):
 * Rename… (F2) · Move… · Manage access… · Propagate access… (directories) · and, in the row menu's ONE Copy submenu
 * (group `copy`, UI/UX fix round A U16), abfss:// URI · DFS URL — the menu's own URL / Path entries already copy the
 * Blob URL and the path, so they are not repeated. Visible only on a Data Lake row — the Data Lake view, or the Blob view of an account whose
 * hierarchical namespace is confirmed — and never on snapshots, historical versions, deleted blobs or pending folders
 * (absent, not disabled: those rows are not Data Lake paths). Read-only connections: every mutating item is disabled
 * with the standard reason; Manage access… stays (it opens view-only); Copy path stays.
 */
import { FolderInput, ListTree, Pencil, ShieldCheck } from 'lucide-react';
import { toast } from '../../../../components/ui/toaster';
import { writeClipboardText } from '../../../../lib/clipboard';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';
import { hnsRefOfItem } from '../../../../core/engines/azureStorageHnsPaths';
import { hnsServiceFacts } from '../../../../core/engines/azureStorageHnsFacts';
import { HNS_MOVE_UNAVAILABLE_REASON, type HnsPathRef } from '../../../../types/azureStorage';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { trackStorageEvent } from '../services/storageAnalytics';
import { registerStorageRowMenuExtension, type StorageRowMenuContext, type StorageRowMenuItem } from '../toolbar/rowMenuExtensions';
import type { StorageBrowserRow } from '../utils/storageItemRows';
import { ADLS_PATH_FORMATS, adlsPathText, type AdlsPathFormat } from './copyAdlsPath';
import type { HnsActionSource } from './dialogs/hnsDialogTypes';

/** True where S8 surfaces apply: the Data Lake view, or the Blob view of a confirmed-HNS account. */
export const isHnsSurface = (ctx: Pick<StorageRowMenuContext, 'mode' | 'hns'>): boolean => ctx.mode === 'hns' || (ctx.mode === 'blobs' && ctx.hns === 'yes');

/** The Data Lake ref of a row, or `undefined` for rows that are not Data Lake paths. */
export function hnsRowRef(row: StorageBrowserRow, ctx: Pick<StorageRowMenuContext, 'mode' | 'hns'>): HnsPathRef | undefined {
  if (!isHnsSurface(ctx) || row.pending) return undefined;
  const item = row.item;
  if (item.kind === 'blob' && (item.snapshot || item.deleted || (item.versionId && item.isCurrentVersion === false))) return undefined;
  return hnsRefOfItem(item);
}

export function hnsMoveDisabledReason(readOnly: boolean): string | undefined {
  if (readOnly) return STORAGE_READ_ONLY_REASON;
  return hnsServiceFacts().moveImplementation === 'disabled' ? HNS_MOVE_UNAVAILABLE_REASON : undefined;
}

/** Opens the Rename / Move dialog for a row (row menu and F2). */
export function openHnsMoveDialog(row: StorageBrowserRow, ctx: StorageRowMenuContext, moveAction: 'rename' | 'move', origin: HnsActionSource, tabId?: string): void {
  const resource = hnsRowRef(row, ctx);
  if (!resource) return;
  const listedEtag = row.item.kind === 'folder' || row.item.kind === 'blob' ? row.item.hns?.etag : undefined;
  ctx.openDialog({ kind: 'hns-move', connectionId: ctx.connectionId, resource, source: 'row-menu', hns: { moveAction, origin, ...(listedEtag ? { listedEtag } : {}), ...(tabId ? { tabId } : {}) } });
}

export function copyHnsPath(connectionId: string, resource: HnsPathRef, format: AdlsPathFormat, source: HnsActionSource): void {
  const { connection } = getConnectionById(connectionId);
  const text = connection ? adlsPathText(connection, resource, format) : undefined;
  if (!text) {
    toast.error({ title: 'Copy path', description: 'No endpoint is known for this format on this connection.' });
    trackStorageEvent('feature:storage_hns_action', { action: 'copy_path', outcome: 'unavailable', source, format });
    return;
  }
  void writeClipboardText(text);
  trackStorageEvent('feature:storage_hns_action', { action: 'copy_path', outcome: 'ok', source, format });
}

const visible = (row: StorageBrowserRow, ctx: StorageRowMenuContext): boolean => hnsRowRef(row, ctx) !== undefined;

export const HNS_ROW_MENU_ITEMS: readonly StorageRowMenuItem[] = [
  {
    id: 's8-rename', order: 800, testId: 'storage-row-menu-s8-rename', label: 'Rename…', icon: Pencil, shortcut: 'F2', isVisible: visible,
    disabledReason: (_row, ctx) => hnsMoveDisabledReason(ctx.readOnly),
    onSelect: (row, ctx) => openHnsMoveDialog(row, ctx, 'rename', 'row-menu'),
  },
  {
    id: 's8-move', order: 810, testId: 'storage-row-menu-s8-move', label: 'Move…', icon: FolderInput, isVisible: visible,
    disabledReason: (_row, ctx) => hnsMoveDisabledReason(ctx.readOnly),
    onSelect: (row, ctx) => openHnsMoveDialog(row, ctx, 'move', 'row-menu'),
  },
  {
    id: 's8-manage-access', order: 820, testId: 'storage-row-menu-s8-manage-access', label: 'Manage access…', icon: ShieldCheck, isVisible: visible,
    onSelect: (row, ctx) => {
      const resource = hnsRowRef(row, ctx);
      if (!resource) return;
      trackStorageEvent('toolbar:storage_manage_access', { source: 'row-menu' });
      ctx.openDialog({ kind: 'hns-acl', connectionId: ctx.connectionId, resource, source: 'row-menu', hns: { origin: 'row-menu' } });
    },
  },
  {
    id: 's8-propagate-access', order: 830, testId: 'storage-row-menu-s8-propagate-access', label: 'Propagate access…', icon: ListTree,
    isVisible: (row, ctx) => hnsRowRef(row, ctx)?.isDirectory === true,
    disabledReason: (_row, ctx) => (ctx.readOnly ? STORAGE_READ_ONLY_REASON : undefined),
    onSelect: (row, ctx) => {
      const resource = hnsRowRef(row, ctx);
      if (resource) ctx.openDialog({ kind: 'hns-acl-propagate', connectionId: ctx.connectionId, resource, source: 'row-menu', hns: { origin: 'row-menu' } });
    },
  },
  {
    id: 's8-copy-path', order: 840, testId: 'storage-row-menu-s8-copy-path', label: 'Copy', group: 'copy', isVisible: visible,
    submenu: ADLS_PATH_FORMATS.filter(({ format }) => format === 'abfss' || format === 'dfs').map(({ format, label }) => ({
      id: `s8-copy-path-${format}`, label, testId: `storage-row-menu-s8-copy-path-${format}`,
      onSelect: (row: StorageBrowserRow, ctx: StorageRowMenuContext) => {
        const resource = hnsRowRef(row, ctx);
        if (resource) copyHnsPath(ctx.connectionId, resource, format, 'row-menu');
      },
    })),
  },
];

export function registerHnsRowMenuItems(): () => void {
  const unregister = HNS_ROW_MENU_ITEMS.map((item) => registerStorageRowMenuExtension(item));
  return () => unregister.forEach((fn) => fn());
}
